import { useEffect, useRef, useState } from 'react'
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps'
import { env } from '@/config/env'
import {
  shiftLatLng,
  needToShowFirstPoint,
  getDottedPoints,
  getPolylineCenter,
  calculateDistance,
  calculateDeviation,
  closestPointIndex,
} from '@/lib/geo'
import { isPossitionValid } from '@/lib/validate'
import { serializeLatLng } from '@/lib/latlng'
import { convertDistanceToText } from '@/lib/format'
import { dirDistance, directionModelFromMap } from '@/types/structs'
import { appState } from '@/store/appState'
import { callGetDirections } from '@/firebase/functions'
import { useToast } from '@/components/ui/ToastProvider'
import { tr, locale } from '@/i18n'
import { SquareCircleSpinner } from '@/components/ui/Spinner'
import { dispatchMapUpdate } from './dispatch'
import { CurrentMarker } from './markers/CurrentMarker'
import { DestinationMarker } from './markers/DestinationMarker'
import { FirstPointMarker } from './markers/FirstPointMarker'
import { DistanceCallout } from './markers/DistanceCallout'
import { useRoutePolyline, useDottedCircles } from './overlays/useRouteOverlays'
import {
  CAMERA_SHIFT_DIVISOR,
  MAP_ZOOM,
  MAX_DIST_CIRCLES_M,
  DEVIATION_M,
  DOT_STEP_M,
} from '@/config/constants'
import type { LatLng } from '@/types/structs'
import type { DirectionFor, BottomSheetView } from '@/types/enums'

/* Port of custom_code/widgets/custom_g_map.dart — map base (M8) + route
 * engine (M9).
 *
 * Parity mechanics preserved:
 *  - _currentLocation is INTERNAL state seeded once from props; outside the
 *    directions view a currentLocation prop change does NOT update it;
 *  - `// _updateRoute();` in initState is commented out — no route on mount;
 *  - currentZoom lives in a ref (onCameraMove without setState): the
 *    firstPoint threshold applies only on the NEXT marker rebuild;
 *  - didUpdateWidget dispatch — mutually exclusive, order matters (below);
 *  - _updateRoute: three parallel getDirections calls, per-mode models
 *    written SILENTLY + ONE publish; exceptions only logged in dev; a mode
 *    without distance/duration KEEPS the previous model;
 *  - walk gate: distance.value < 20000 -> dotted circles (12 m step, radius
 *    4 m, #1F4CF1 fill / #0919C7 stroke), else polyline width 5;
 *  - _updatePolyline: deviation > 100 m -> full recalc; else trim from the
 *    closest vertex, mutate model.distance IN PLACE (silent), move callout.
 *    ORIGINAL BUG (walk-circle mode read _polylines.first from an empty set
 *    -> 'Bad state: No element' crash every tick) is FIXED by measuring
 *    deviation against _routePoints (flags.PARITY_WALK_TICK_CRASH);
 *  - route geometry is VISIBLE ONLY in the directions view (render gates);
 *  - _routePoints is never trimmed — sublist() result was never assigned. */

export interface CustomGMapProps {
  currentLocation: LatLng | null
  destination: LatLng
  polylineColor: string
  directionFor: DirectionFor
  bottomSheetView: BottomSheetView
  userName: string
  firstPoinLocation: LatLng // (sic)
}

export function CustomGMap(props: CustomGMapProps) {
  return (
    <APIProvider apiKey={env.mapsKey}>
      <MapInner {...props} />
    </APIProvider>
  )
}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined)
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

interface RouteState {
  polyline: LatLng[] | null
  circles: LatLng[]
  callout: LatLng | null
}

function MapInner(props: CustomGMapProps) {
  const map = useMap()
  const toast = useToast()

  const [currentLocation, setCurrentLocation] = useState<LatLng | null>(
    props.currentLocation,
  )
  const currentLocationRef = useRef(currentLocation) // sync source for async engine
  const [autoCenter, setAutoCenter] = useState(true)
  const autoCenterRef = useRef(true)
  autoCenterRef.current = autoCenter
  const currentZoom = useRef(MAP_ZOOM)
  const [isLoadingDirections, setLoadingDirections] = useState(false)
  const [route, setRoute] = useState<RouteState>({
    polyline: null,
    circles: [],
    callout: null,
  })
  const routePointsRef = useRef<LatLng[]>([])
  const propsRef = useRef(props)
  propsRef.current = props

  const screenH = typeof window !== 'undefined' ? window.innerHeight : 800

  const moveToLocation = (loc: LatLng, en?: boolean) => {
    const enable = en ?? autoCenterRef.current
    if (map && enable) {
      map.panTo(loc)
      map.panBy(0, screenH / CAMERA_SHIFT_DIVISOR)
    }
  }

  // ---- _updateRoute ------------------------------------------------------
  async function updateRoute(showLoader = true, _dbgTrigger = '?'): Promise<void> {
    if (import.meta.env.DEV) {
      const w = window as unknown as { __routeDbg?: string[] }
      ;(w.__routeDbg ??= []).push(`updateRoute:${_dbgTrigger}@${Math.round(performance.now())}`)
    }
    const p = propsRef.current
    const cur = currentLocationRef.current

    if (!isPossitionValid(cur)) {
      setRoute((s) => ({ ...s, callout: null })) // marker rebuild drops "distance"
      setLoadingDirections(false)
      if (p.bottomSheetView === 'directions') {
        toast.show(tr.toasts.turnOnGeolocation)
      }
      return
    }

    const origin = serializeLatLng(cur!)
    const destination = serializeLatLng(p.destination)
    if (showLoader) setLoadingDirections(true)

    type DirData = Awaited<ReturnType<typeof callGetDirections>>
    let results: [DirData, DirData, DirData] | null = null
    try {
      results = (await Promise.all([
        callGetDirections({ origin, destination, mode: 'walking', language: locale }),
        callGetDirections({ origin, destination, mode: 'bicycling', language: locale }),
        callGetDirections({ origin, destination, mode: 'driving', language: locale }),
      ])) as [DirData, DirData, DirData]
    } catch (e) {
      // FirebaseFunctionsException: logged in debug only; results stays null.
      if (import.meta.env.DEV) console.error('getDirections failed:', e)
    }

    const dataWalking = results?.[0] ?? null
    const dataBicycling = results?.[1] ?? null
    const dataDriving = results?.[2] ?? null

    // Silent writes; a mode without both fields KEEPS its previous model.
    if (dataWalking?.distance != null && dataWalking?.duration != null)
      appState.walkingDirectionModel = directionModelFromMap(dataWalking)
    if (dataBicycling?.distance != null && dataBicycling?.duration != null)
      appState.bicyclingDirectionModel = directionModelFromMap(dataBicycling)
    if (dataDriving?.distance != null && dataDriving?.duration != null)
      appState.drivingDirectionModel = directionModelFromMap(dataDriving)
    appState.update() // ONE publish — switch labels refresh

    const data =
      p.directionFor === 'walk'
        ? dataWalking
        : p.directionFor === 'bicycle'
          ? dataBicycling
          : dataDriving

    if (data?.polyline != null) {
      const points: LatLng[] = data.polyline.map((pt) => ({ lat: pt.lat, lng: pt.lng }))
      routePointsRef.current = points
      const calloutPos = getPolylineCenter(points)

      if (
        p.directionFor === 'walk' &&
        dirDistance(appState.walkingDirectionModel).value < MAX_DIST_CIRCLES_M
      ) {
        setRoute({
          circles: getDottedPoints(points, DOT_STEP_M),
          polyline: null,
          callout: calloutPos,
        })
      } else {
        setRoute({ circles: [], polyline: points, callout: calloutPos })
      }
      setLoadingDirections(false)
    } else {
      // Full failure and "no route for this mode" are indistinguishable —
      // same toast, as in the original. _routePoints is NOT cleared.
      setRoute({ circles: [], polyline: null, callout: null })
      setLoadingDirections(false)
      if (p.bottomSheetView === 'directions') {
        toast.show(tr.toasts.noRouteForType)
      }
    }
  }

  // ---- _updatePolyline (live trim) ---------------------------------------
  async function updatePolyline(current: LatLng): Promise<void> {
    const pts = routePointsRef.current
    if (pts.length === 0) return

    // FIX (PARITY_WALK_TICK_CRASH=false): the original measured deviation
    // against _polylines.first — empty in walk-circle mode -> crash. We
    // measure against _routePoints, which is what the trim uses anyway.
    const deviation = calculateDeviation(current, pts)

    if (deviation > DEVIATION_M) {
      void updateRoute(true, 'deviation') // full recalc — exactly one cycle
      return
    }

    const closest = closestPointIndex(current, pts)
    const remaining = pts.slice(closest) // _routePoints itself stays full
    const newDistance = Math.trunc(calculateDistance(remaining))

    // loadDistanceMarkerDescriptor(newDistance): IN-PLACE SILENT mutation of
    // the active model's distance — value becomes the REMAINING length,
    // which also feeds the < 20000 render gate (parity).
    const p = propsRef.current
    const model =
      p.directionFor === 'walk'
        ? appState.walkingDirectionModel
        : p.directionFor === 'bicycle'
          ? appState.bicyclingDirectionModel
          : appState.drivingDirectionModel
    model.distance = { text: convertDistanceToText(newDistance), value: newDistance }

    const calloutPos = getPolylineCenter(remaining)
    setRoute((s) => ({ ...s, polyline: remaining, callout: calloutPos }))
  }

  // ---- didUpdateWidget ---------------------------------------------------
  const prev = usePrevious({
    destination: props.destination,
    directionFor: props.directionFor,
    bottomSheetView: props.bottomSheetView,
    currentLocation: props.currentLocation,
  })
  const [markersVersion, setMarkersVersion] = useState(0)
  useEffect(() => {
    if (!prev) return
    const decision = dispatchMapUpdate(prev, {
      destination: props.destination,
      directionFor: props.directionFor,
      bottomSheetView: props.bottomSheetView,
      currentLocation: props.currentLocation,
    })
    switch (decision) {
      case 'updateRoute':
        // (a) NOTE: _currentLocation deliberately NOT synced here (parity —
        // route computed from the possibly stale origin).
        void updateRoute(true, 'branch-a')
        break
      case 'enterDirections':
        // (b) just entered directions
        currentLocationRef.current = props.currentLocation
        setCurrentLocation(props.currentLocation)
        if (isPossitionValid(props.currentLocation)) {
          moveToLocation(props.currentLocation!)
          void updateRoute(true, 'branch-b')
        }
        break
      case 'locationTick':
        // (c) location tick in directions
        currentLocationRef.current = props.currentLocation
        setCurrentLocation(props.currentLocation)
        if (isPossitionValid(props.currentLocation)) {
          moveToLocation(props.currentLocation!)
          void updatePolyline(props.currentLocation!)
        }
        break
      case 'rebuildAndPan':
        setMarkersVersion((v) => v + 1)
        moveToLocation(props.destination, true) // FORCED
        break
      case 'none':
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.destination, props.directionFor, props.bottomSheetView, props.currentLocation])

  // ---- initial camera ----------------------------------------------------
  const initialCenter = useRef(
    shiftLatLng(
      props.bottomSheetView === 'directions'
        ? props.currentLocation! // Dart force-unwraps here too
        : props.destination,
      screenH / CAMERA_SHIFT_DIVISOR,
      MAP_ZOOM,
    ),
  )

  // ---- auto-centre -------------------------------------------------------
  const onPointerMove = () => {
    if (!autoCenterRef.current) return
    if (isPossitionValid(props.currentLocation)) setAutoCenter(false)
  }
  const recenter = () => {
    if (autoCenterRef.current) return
    setAutoCenter(true)
    if (isPossitionValid(props.currentLocation)) {
      moveToLocation(props.currentLocation!, true)
    }
  }

  // ---- render gates (build) ----------------------------------------------
  const inDirections = props.bottomSheetView === 'directions'
  const walkingValue = dirDistance(appState.walkingDirectionModel).value
  const showPolyline =
    (props.directionFor !== 'walk' && inDirections) ||
    (props.directionFor === 'walk' && inDirections && walkingValue >= MAX_DIST_CIRCLES_M)
  const showCircles =
    props.directionFor === 'walk' && inDirections && walkingValue < MAX_DIST_CIRCLES_M
  const showCallout =
    inDirections && route.callout != null && isPossitionValid(currentLocation)

  useRoutePolyline(map, route.polyline, showPolyline, props.polylineColor)
  useDottedCircles(map, route.circles, showCircles, props.polylineColor)

  if (import.meta.env.DEV) {
    ;(window as unknown as Record<string, unknown>).__mapDbg = {
      polylinePoints: route.polyline?.length ?? 0,
      circleCount: route.circles.length,
      routePoints: routePointsRef.current.length,
      showPolyline,
      showCircles,
      walkingValue,
      callout: route.callout,
    }
  }

  void markersVersion

  return (
    <div data-gmap style={{ position: 'absolute', inset: 0 }} onPointerMove={onPointerMove}>
      <Map
        mapId={env.mapId}
        defaultCenter={initialCenter.current}
        defaultZoom={MAP_ZOOM}
        zoomControl={false}
        streetViewControl={false}
        mapTypeControl={false}
        fullscreenControl={false}
        onCameraChanged={(e) => {
          currentZoom.current = e.detail.zoom // no setState — parity
        }}
        style={{ width: '100%', height: '100%' }}
      >
        {isPossitionValid(currentLocation) && <CurrentMarker position={currentLocation!} />}
        {showCallout && (
          <DistanceCallout position={route.callout!} directionFor={props.directionFor} />
        )}
        {needToShowFirstPoint(
          props.firstPoinLocation,
          props.destination,
          currentZoom.current,
        ) && <FirstPointMarker position={props.firstPoinLocation} />}
        <DestinationMarker position={props.destination} userName={props.userName} />
      </Map>

      {/* SpinKitSquareCircle. VERIFIED against flutter_spinkit-5.2.2 source:
          the widget wraps itself in Center(...), so as an unpositioned Stack
          child it expands and renders CENTERED over the map — no backdrop,
          no input blocking. (Earlier assumption "top-left" was wrong.) */}
      {isLoadingDirections && (
        <div
          data-directions-loader
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <SquareCircleSpinner />
        </div>
      )}

      {/* Recentre FAB (fade 500 ms; pointer-events fix for the original's
          transparent-but-clickable bug). */}
      <button
        type="button"
        data-recenter-fab
        onClick={recenter}
        style={{
          position: 'absolute',
          right: 24,
          top: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#ffffff',
          boxShadow:
            '0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: autoCenter ? 0 : 1,
          transition: 'opacity 500ms',
          pointerEvents: autoCenter ? 'none' : 'auto',
          cursor: 'pointer',
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill={autoCenter ? '#2196F3' : 'rgba(0,0,0,.54)'}
        >
          <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
        </svg>
      </button>
    </div>
  )
}
